import React, { useState } from "react";
import api from "../../services/axiosInstance";
import { FaTimes,FaArrowLeft, FaEnvelope, FaKey, FaCheckCircle, FaLock } from "react-icons/fa";

const ForgotPasswordModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const [currentStep, setCurrentStep] = useState("email"); // "email", "otp", "reset"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  if (!isOpen) return null;

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/users/forgot-password/send-otp", { email });
      setMessage(res.data.message);
      setCurrentStep("otp");
    } catch (err) {
      setMessage(err.response?.data?.message || "Erreur lors de l'envoi du code.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setMessage("Veuillez saisir le code à 6 chiffres.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/users/forgot-password/verify-otp", { email, otp });
      setMessage(res.data.message);
      setResetToken(res.data.resetToken);
      setCurrentStep("reset");
    } catch (err) {
      setMessage(err.response?.data?.message || "Code invalide.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      setMessage("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/users/forgot-password/reset", {
        resetToken,
        newPassword,
        email
      });
      
      setMessage(res.data.message);
      setTimeout(() => {
        onClose();
        onSwitchToLogin();
      }, 2000);
    } catch (err) {
      setMessage(err.response?.data?.message || "Erreur lors de la réinitialisation.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep === "otp") {
      setCurrentStep("email");
      setOtp("");
    } else if (currentStep === "reset") {
      setCurrentStep("otp");
      setNewPassword("");
      setConfirmPassword("");
    }
    setMessage("");
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 animate-[fadeIn_0.2s_ease]">
  <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-xl shadow-lg animate-[slideUp_0.3s_ease] relative">
    
    {/* Header */}
    <div className="px-6 py-4 border-b border-emerald-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {currentStep !== "email" && (
            <button 
              onClick={handleBack}
              className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
            >
              <FaArrowLeft className="text-sm" />
            </button>
          )}
          <h2 className="text-lg font-semibold text-emerald-900">
            {currentStep === "email" && "Reset Password"}
            {currentStep === "otp" && "Verification"}
            {currentStep === "reset" && "New Password"}
          </h2>
        </div>
        <button 
          onClick={onClose}
          className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
        >
          <FaTimes className="text-sm" />
        </button>
      </div>
      <p className="text-emerald-600 text-xs mt-1">
        {currentStep === "email" && "Enter your email to receive a reset code"}
        {currentStep === "otp" && `Code sent to ${email}`}
        {currentStep === "reset" && "Choose your new password"}
      </p>
    </div>

    {/* Body */}
    <div className="p-6">
      {/* Step 1: Email Input */}
      {currentStep === "email" && (
        <div className="space-y-4">
          <div className="text-center mb-3">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaEnvelope className="text-lg text-emerald-600" />
            </div>
            <p className="text-gray-600 text-sm">
              We'll send a verification code to your email address.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">
              Email Address
            </label>
            <div className="relative">
              <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg 
                focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all 
                bg-white placeholder-gray-400 text-sm"
              />
            </div>
          </div>

          <button
            onClick={handleSendOTP}
            disabled={loading || !email}
            className="w-full bg-emerald-600 hover:bg-emerald-700 
            text-white py-3 rounded-lg font-medium text-sm transition-colors
            flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "Sending..." : "Send Code"}
          </button>
        </div>
      )}

      {/* Step 2: OTP Verification */}
      {currentStep === "otp" && (
        <div className="space-y-5">
          <div className="text-center">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaCheckCircle className="text-lg text-emerald-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-1">
              Enter Verification Code
            </h3>
            <p className="text-gray-600 text-sm">
              Sent to <span className="font-medium text-emerald-700">{email}</span>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                6-digit Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength="6"
                className="w-full border border-gray-300 rounded-lg py-3 px-4 text-center 
                text-xl font-medium tracking-widest focus:ring-1 focus:ring-emerald-500 
                focus:border-emerald-500 transition-all text-sm"
                placeholder="123456"
                autoFocus
              />
            </div>

            <button
              onClick={handleVerifyOTP}
              disabled={loading || otp.length !== 6}
              className="w-full bg-emerald-600 hover:bg-emerald-700 
              text-white py-3 rounded-lg font-medium text-sm transition-colors
              flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Verify Code"}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: New Password */}
      {currentStep === "reset" && (
        <div className="space-y-4">
          <div className="text-center mb-3">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaKey className="text-lg text-emerald-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-1">
              Create New Password
            </h3>
            <p className="text-gray-600 text-sm">
              Choose a secure password
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                New Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg 
                  focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all 
                  bg-white placeholder-gray-400 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Confirm Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  required
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg 
                  focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all 
                  bg-white placeholder-gray-400 text-sm"
                />
              </div>
            </div>

            <button
              onClick={handleResetPassword}
              disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
              className="w-full bg-emerald-600 hover:bg-emerald-700 
              text-white py-3 rounded-lg font-medium text-sm transition-colors
              flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div
          className={`mt-4 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-300 ${
            message.startsWith("✅") || message.includes("success")
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {message}
        </div>
      )}

      {/* Back to Login */}
      {currentStep === "email" && (
        <div className="text-center mt-5 pt-4 border-t border-gray-100">
          <p className="text-gray-600 text-xs">
            Back to{" "}
            <button
              onClick={onSwitchToLogin}
              className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
            >
              sign in
            </button>
          </p>
        </div>
      )}
    </div>
  </div>
</div> 
  );
};

export default ForgotPasswordModal;